import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogContentText,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import User from '../../models/User';
import { updateUser, deleteUser } from '../../services/ApiService';
import { useQueryClient } from '@tanstack/react-query';
import { useSystemTables } from '../../hooks/UseSystemTables';

interface EditUserDialogProps {
  user: User;
  open: boolean;
  onClose: () => void;
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({ user, open, onClose }) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { data: systemTablesData } = useSystemTables();
  const [formData, setFormData] = useState({
    phone_number: user.phone_number || '',
    email: user.email || '',
    'custom:clientInnerId': user['custom:clientInnerId'] || '',
    'custom:role': user['custom:role'] || '',
    'custom:app': user['custom:app'] || '',
    'custom:deletionProtected': user['custom:deletionProtected'] === 'true',
    'custom:createUserWhatsapp': user['custom:createUserWhatsapp'] === 'yes',
    'custom:orderingWhatsapp': user['custom:orderingWhatsapp'] === 'yes',
    'custom:siloWhatsapp': user['custom:siloWhatsapp'] === 'yes',
    'custom:assignedProduct': user['custom:assignedProduct'] || '',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCheckboxChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsUpdating(true);
      // Convert boolean values to strings before sending
      const submitData = {
        ...formData,
        'custom:deletionProtected': formData['custom:deletionProtected'].toString(),
        'custom:createUserWhatsapp': formData['custom:createUserWhatsapp'] ? 'yes' : 'no',
        'custom:orderingWhatsapp': formData['custom:orderingWhatsapp'] ? 'yes' : 'no',
        'custom:siloWhatsapp': formData['custom:siloWhatsapp'] ? 'yes' : 'no',
      };
      
      await updateUser(user.username, submitData);
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteUser(user.username);
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteDialogOpen(false);
      onClose();
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const clients = systemTablesData?.data.clients || [];
  const products = systemTablesData?.data.catalog || [];
  const userRoles = systemTablesData?.data.systemParameters?.userRole || [];
  const appTypes = systemTablesData?.data.systemParameters?.appType || [];

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            m: 1,
            width: '100%',
            maxWidth: '800px',
          }
        }}
      >
        <DialogTitle>{t('EditUser.Title')}</DialogTitle>
        <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('User.PhoneNumber')}
                  value={formData.phone_number}
                  onChange={handleChange('phone_number')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('User.Email')}
                  value={formData.email}
                  onChange={handleChange('email')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('User.ClientInnerId')}</InputLabel>
                  <Select
                    value={formData['custom:clientInnerId']}
                    label={t('User.ClientInnerId')}
                    onChange={(e) => setFormData(prev => ({ ...prev, 'custom:clientInnerId': e.target.value }))}
                  >
                    {clients.map((client) => (
                      <MenuItem key={client.clientInnerId} value={client.clientInnerId}>
                        {client.clientName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('User.Role')}</InputLabel>
                  <Select
                    value={formData['custom:role']}
                    label={t('User.Role')}
                    onChange={(e) => setFormData(prev => ({ ...prev, 'custom:role': e.target.value }))}
                  >
                    {userRoles.map((role) => (
                      <MenuItem key={role._id} value={role.parameterValue}>
                        {i18n.language === 'he' ? role.hebName : role.engName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('User.App')}</InputLabel>
                  <Select
                    value={formData['custom:app']}
                    label={t('User.App')}
                    onChange={(e) => setFormData(prev => ({ ...prev, 'custom:app': e.target.value }))}
                  >
                    {appTypes.map((app) => (
                      <MenuItem key={app._id} value={app.parameterValue}>
                        {i18n.language === 'he' ? app.hebName : app.engName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('User.AssignedProduct')}</InputLabel>
                  <Select
                    value={formData['custom:assignedProduct']}
                    label={t('User.AssignedProduct')}
                    onChange={(e) => setFormData(prev => ({ ...prev, 'custom:assignedProduct': e.target.value }))}
                  >
                    <MenuItem value="null">
                      <em>{t('General.Labels.None')}</em>
                    </MenuItem>
                    {products.map((product) => (
                      <MenuItem key={product.itemNumber} value={product.itemNumber}>
                        {product.itemNumber}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData['custom:deletionProtected']}
                      onChange={handleCheckboxChange('custom:deletionProtected')}
                      sx={{
                        '&.Mui-checked': {
                          color: '#1976d2',
                        },
                      }}
                    />
                  }
                  label={t('User.DeletionProtected')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData['custom:createUserWhatsapp']}
                      onChange={handleCheckboxChange('custom:createUserWhatsapp')}
                      sx={{
                        '&.Mui-checked': {
                          color: '#1976d2',
                        },
                      }}
                    />
                  }
                  label={t('User.CreateUserWhatsapp')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData['custom:orderingWhatsapp']}
                      onChange={handleCheckboxChange('custom:orderingWhatsapp')}
                      sx={{
                        '&.Mui-checked': {
                          color: '#1976d2',
                        },
                      }}
                    />
                  }
                  label={t('User.OrderingWhatsapp')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData['custom:siloWhatsapp']}
                      onChange={handleCheckboxChange('custom:siloWhatsapp')}
                      sx={{
                        '&.Mui-checked': {
                          color: '#1976d2',
                        },
                      }}
                    />
                  }
                  label={t('User.SiloWhatsapp')}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(true)}
            variant="contained" 
            color="error"
            disabled={isUpdating}
            sx={{ 
              mr: 'auto',
              backgroundColor: '#d32f2f',
              '&:hover': {
                backgroundColor: '#b71c1c',
              },
              '&:active': {
                backgroundColor: '#7f0000',
              }
            }}
          >
            {t('General.Labels.Delete')}
          </Button>
          <Button 
            onClick={onClose} 
            variant="outlined" 
            color="error"
            disabled={isUpdating}
            sx={{ mr: 1 }}
          >
            {t('General.Labels.Cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {t('General.Labels.Confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('EditUser.DeleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('EditUser.DeleteConfirmation', { username: user.username })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            variant="outlined"
            color="error"
            disabled={isDeleting}
          >
            {t('General.Labels.Cancel')}
          </Button>
          <Button 
            onClick={handleDelete} 
            variant="contained" 
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ 
              backgroundColor: '#d32f2f',
              '&:hover': {
                backgroundColor: '#b71c1c',
              },
              '&:active': {
                backgroundColor: '#7f0000',
              }
            }}
          >
            {t('General.Labels.Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditUserDialog; 